import * as React from "react";
import { ChevronDown, ChevronRight, Server, AlertCircle, AlertTriangle, CheckCircle } from "react-feather";
import { useAppSelector } from "../../store/hooks";
import { Loader } from "../../components/layout/loader";
import { ProcessedWorkload, selectProcessedGvcs } from "../../store/healthSlice";
import { InternalLink } from "../../newcomponents/table/components/InternalLink";
import { linksOf } from "../../services/cpln";
import { HEALTH_COLORS } from "./utils";

export const Workloads: React.FC = () => {
  const isLoading = useAppSelector((state) => state.health.isLoading);
  const isFetched = useAppSelector((state) => state.health.isFetched);
  const gvcs = useAppSelector(selectProcessedGvcs);

  const [expandedGvc, setExpandedGvc] = React.useState<string | null>(null);

  // Calculate totals
  const totals = React.useMemo(() => {
    let ready = 0;
    let warning = 0;
    let error = 0;
    for (const gvc of gvcs) {
      ready += gvc.workloads.ready.length + gvc.workloads.suspended.length + gvc.workloads.partiallySuspended.length;
      warning += gvc.workloads.notLatest.length;
      error += gvc.workloads.notReady.length + gvc.workloads.syncFailed.length;
    }
    return { ready, warning, error, total: ready + warning + error };
  }, [gvcs]);

  if (isLoading && !isFetched) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader />
      </div>
    );
  }

  return (
    <div className="pb-16" style={{ maxWidth: 1000 }}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Server size={20} style={{ color: "var(--color-label)" }} />
          <h1 className="text-xl font-semibold" style={{ color: "var(--color-default)" }}>
            Workload Health
          </h1>
        </div>
        <div className="flex items-center gap-4 text-sm" style={{ color: "var(--color-label)" }}>
          <span className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: HEALTH_COLORS.ready }} />
            {totals.ready} ready
          </span>
          {totals.warning > 0 && (
            <span className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: HEALTH_COLORS.warning }} />
              {totals.warning} warning
            </span>
          )}
          {totals.error > 0 && (
            <span className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: HEALTH_COLORS.error }} />
              {totals.error} error
            </span>
          )}
        </div>
      </div>

      {/* GVC List */}
      <div className="space-y-3">
        {gvcs.map((gvc) => {
          const selfLink = linksOf(gvc.item).self!;
          const totalWorkloads =
            gvc.workloads.ready.length +
            gvc.workloads.notReady.length +
            gvc.workloads.notLatest.length +
            gvc.workloads.syncFailed.length +
            gvc.workloads.suspended.length +
            gvc.workloads.partiallySuspended.length;

          const healthyCount =
            gvc.workloads.ready.length + gvc.workloads.suspended.length + gvc.workloads.partiallySuspended.length;
          const warningCount = gvc.workloads.notLatest.length;
          const errorCount = gvc.workloads.notReady.length + gvc.workloads.syncFailed.length;

          const hasIssues = errorCount > 0 || warningCount > 0;
          const isExpanded = expandedGvc === gvc.item.name;

          const getStatusIcon = () => {
            if (errorCount > 0) return <AlertCircle size={16} color={HEALTH_COLORS.error} />;
            if (warningCount > 0) return <AlertTriangle size={16} color={HEALTH_COLORS.warning} />;
            return <CheckCircle size={16} color={HEALTH_COLORS.ready} />;
          };

          return (
            <div
              key={selfLink}
              className="rounded-lg overflow-hidden"
              style={{
                backgroundColor: "var(--color-drop)",
                border: `1px solid ${errorCount > 0 ? HEALTH_COLORS.error : warningCount > 0 ? HEALTH_COLORS.warning : "var(--color-border)"}`,
              }}
            >
              {/* GVC Header */}
              <div
                className={`px-4 py-3 ${hasIssues ? "cursor-pointer" : ""}`}
                onClick={() => hasIssues && setExpandedGvc(isExpanded ? null : gvc.item.name)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {hasIssues && (
                      <div style={{ color: "var(--color-label)" }}>
                        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </div>
                    )}
                    <InternalLink
                      label={gvc.item.name}
                      to={`/console${selfLink}`}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className="text-xs" style={{ color: "var(--color-label)" }}>
                      {healthyCount}/{totalWorkloads} ready
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {errorCount > 0 && (
                      <span
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: "rgba(239, 68, 68, 0.1)", color: HEALTH_COLORS.error }}
                      >
                        {errorCount} error
                      </span>
                    )}
                    {warningCount > 0 && (
                      <span
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: "rgba(245, 158, 11, 0.1)", color: HEALTH_COLORS.warning }}
                      >
                        {warningCount} warning
                      </span>
                    )}
                    {getStatusIcon()}
                  </div>
                </div>

                {/* Progress Bar */}
                <div
                  className="h-1.5 rounded-full overflow-hidden flex mt-2"
                  style={{ backgroundColor: "var(--color-border)" }}
                >
                  {healthyCount > 0 && (
                    <div
                      className="h-full"
                      style={{
                        width: `${(healthyCount / totalWorkloads) * 100}%`,
                        backgroundColor: HEALTH_COLORS.ready,
                      }}
                    />
                  )}
                  {warningCount > 0 && (
                    <div
                      className="h-full"
                      style={{
                        width: `${(warningCount / totalWorkloads) * 100}%`,
                        backgroundColor: HEALTH_COLORS.warning,
                      }}
                    />
                  )}
                  {errorCount > 0 && (
                    <div
                      className="h-full"
                      style={{
                        width: `${(errorCount / totalWorkloads) * 100}%`,
                        backgroundColor: HEALTH_COLORS.error,
                      }}
                    />
                  )}
                </div>
              </div>

              {/* Expanded Workload Details */}
              {isExpanded && hasIssues && (
                <div className="px-4 pb-4" style={{ borderTop: "1px solid var(--color-border)" }}>
                  {gvc.workloads.syncFailed.length > 0 && (
                    <WorkloadGroup title="Sync Failed" workloads={gvc.workloads.syncFailed} color={HEALTH_COLORS.error} />
                  )}
                  {gvc.workloads.notReady.length > 0 && (
                    <WorkloadGroup title="Not Ready" workloads={gvc.workloads.notReady} color={HEALTH_COLORS.error} />
                  )}
                  {gvc.workloads.notLatest.length > 0 && (
                    <WorkloadGroup title="Not Latest Version" workloads={gvc.workloads.notLatest} color={HEALTH_COLORS.warning} />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {gvcs.length === 0 && (
        <div
          className="rounded-lg p-8 text-center"
          style={{ backgroundColor: "var(--color-drop)", border: "1px solid var(--color-border)" }}
        >
          <div className="text-sm" style={{ color: "var(--color-label)" }}>
            No GVCs found
          </div>
        </div>
      )}
    </div>
  );
};

interface WorkloadGroupProps {
  title: string;
  workloads: ProcessedWorkload[];
  color: string;
}

const WorkloadGroup: React.FC<WorkloadGroupProps> = ({ title, workloads, color }) => {
  return (
    <div className="mt-4">
      <div className="text-xs font-medium mb-2" style={{ color }}>
        {title} ({workloads.length})
      </div>
      <div className="space-y-2">
        {workloads.map((workload) => {
          const message = workload.messages[0];
          return (
            <div
              key={workload.selfLink}
              className="rounded p-3 text-sm"
              style={{ backgroundColor: "var(--color-border)" }}
            >
              <div className="flex items-center justify-between mb-1">
                <InternalLink label={workload.item.name} to={`/console${workload.selfLink}`} />
                <div className="flex items-center gap-2 text-xs" style={{ color: "var(--color-label)" }}>
                  {message?.location && <span>{message.location}</span>}
                  <span>v{workload.expectedDeploymentVersion}</span>
                </div>
              </div>
              {message?.text && (
                <div className="text-xs mt-1" style={{ color: "var(--color-label)" }}>
                  {message.text}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
